import { PropertyInfo, PropertyModel } from './property-chain-parser';

interface ForReferenceInfo {
  variable: string;
  fullPath: string;
}

export class SuggestionGenerator {
  constructor(private _propertyModel: PropertyModel) {}

  generateSuggestions(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
    context: monaco.languages.CompletionContext,
  ): monaco.languages.CompletionItem[] {
    const propertyChain = this.getPropertyChain(model, position);
    const isTriggered = this.isTriggered(context);

    const currentChain = !isTriggered && propertyChain == null ? null : propertyChain;

    const pos = model.getWordUntilPosition(position);
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: pos.startColumn,
      endColumn: pos.endColumn,
    };

    const suggestions: monaco.languages.CompletionItem[] = [];

    if (currentChain == null) {
      // select top-level objects (variables)
      Object.keys(this._propertyModel).forEach((key) => {
        this._propertyModel[key].forEach((prop) => {
          if (prop.kind === monaco.languages.CompletionItemKind.Variable) {
            suggestions.push({
              label: prop.name,
              kind: prop.kind,
              insertText: prop.name,
              detail: prop.label,
              documentation: `Object ${prop.name}`,
              range,
            });
          }
        });
      });
    } else if (this._propertyModel[currentChain] != null) {
      // select properties of the current object
      this._propertyModel[currentChain].forEach((prop) => {
        if (prop.kind === monaco.languages.CompletionItemKind.Property) {
          suggestions.push({
            label: prop.name,
            kind: prop.kind,
            insertText: prop.name,
            detail: prop.label,
            documentation: `Property of ${currentChain}`,
            range,
          });
        }
      });
    }

    if (suggestions.length === 0) {
      const forRefInfo = this.getForReferenceInfo(model, position, propertyChain);
      if (forRefInfo != null) {
        const refs = this.getReferencePropertyInfo(model, position, forRefInfo.fullPath);
        if (refs != null) {
          refs.forEach((prop) => {
            suggestions.push({
              label: prop.name,
              kind: prop.kind,
              insertText: prop.name,
              detail: prop.label.replace(/\@\./g, ''),
              documentation: `Property of ${forRefInfo.fullPath}`,
              range,
            });
          });
        }
      }
    }
    return suggestions;
  }

  private isTriggered(context: monaco.languages.CompletionContext): boolean {
    return (monaco.languages.CompletionItemKind.Function as number) === context.triggerKind;
  }

  private getPropertyChain(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
  ): string | null {
    const textBeforeCursor = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });
    const reg = new RegExp(/([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\.[^ ]*?$/);
    const match = reg.exec(textBeforeCursor);
    const propertyChain = match != null ? match[1] : null;
    return propertyChain;
  }

  private getReferencePropertyInfo(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
    path: string,
    level: number = 0,
  ): PropertyInfo[] | null {
    const refs = this._propertyModel[`${path}.@`];
    if (refs != null) {
      return refs;
    } else if (level < 10) {
      // get parent reference info of nested for loop(s)
      const rootVar = path.split('.')[0];
      if (rootVar != null) {
        const parentForRef = this.getForReferenceInfo(model, position, rootVar);
        if (parentForRef != null) {
          const arrayPropertyChain = path.split('.').slice(1).join('.');
          return this.getReferencePropertyInfo(
            model,
            position,
            `${parentForRef.fullPath}.@.${arrayPropertyChain}`,
            level + 1,
          );
        }
      }
    }
    return null;
  }

  private getForReferenceInfo(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
    matchVariable: string | null = null,
  ): ForReferenceInfo | null {
    // search for 'for' statements starting from current line and return its variable and path if found
    // ie: {{ for [variable] in [path] }}
    let currentLine = position.lineNumber;
    while (currentLine >= 1) {
      const lineRange = {
        startLineNumber: currentLine,
        startColumn: 1,
        endLineNumber: currentLine,
        endColumn: model.getLineMaxColumn(currentLine),
      };
      const text = model.getValueInRange(lineRange);
      const reg = new RegExp(/{{\s*for\s+([a-zA-Z0-9_]+)\s+in\s+([a-zA-Z0-9_.@]+)\s*}}/g);

      let match;
      while ((match = reg.exec(text)) !== null) {
        if (matchVariable == null || match[1] === matchVariable) {
          return { variable: match[1], fullPath: match[2] };
        }
      }

      currentLine--;
    }

    return null;
  }
}
